import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AgencyProfileDetails, LegacyUserAgentDetails } from '@rma/dashboard/generic/domain/to-be-sorted';
import { isIndividual } from '@rma/generic/domain/helpers';
import { ContextSelectorService, CurrentUserService } from '@rma/generic/ng/user';
import { appendToRmaApiUrl } from '@rma/generic/ng/util';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { cache } from 'decorator-cache-getter';
import { catchError, combineLatest, firstValueFrom, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { UserProfile } from '../../entities/legacy/user-profile.model';
import { AgencyProfileService } from './agency-profile.service';

/**
 * @deprecated Please use DashboardProfileUserContextService
 *
 */
@Injectable({
  providedIn: 'root',
})
export class LegacyUserService {
  private readonly httpClient = inject(HttpClient);
  private readonly rmaApiUrl = inject(appendToRmaApiUrl);

  private userProfile$ = new ReplaySubject<UserProfile | null>(1);

  constructor(
    private readonly contextSelector: ContextSelectorService,
    private readonly agencyProfileService: AgencyProfileService,
    private readonly currentUserService: CurrentUserService,
  ) {}

  public async initialise(promise: Promise<UserProfile | null> | null): Promise<UserProfile | null> {
    if (promise) {
      const user = await promise;
      this.userProfile$.next(user);
      return user;
    } else {
      return await firstValueFrom(
        this.getLegacyCurrentUser().pipe(
          catchError((e) => {
            console.warn('Auth check failed', e);
            return of(null);
          }),
          tap((x) => this.userProfile$.next(x)),
          take(1),
        ),
      );
    }
  }

  public refreshUserInfo(): Observable<UserProfile | null> {
    const reloadedUser$ = combineLatest([this.getLegacyCurrentUser(), this.currentUserService.reloadUser()]).pipe(
      catchError((e) => {
        console.warn('Auth check failed', e);
        return of([null]);
      }),
      tap(([x, _]) => this.userProfile$.next(x)),
      map(([x, _]) => x),
      rmaShareReplay(),
    );
    reloadedUser$.pipe(take(1)).subscribe(); // ensure it happens
    return reloadedUser$;
  }

  @cache
  public get currentProfile$(): Observable<LegacyUserAgentDetails | AgencyProfileDetails> {
    return combineLatest([this.userInfo$.pipe(nonNullable()), this.contextSelector.selectedContext$.pipe(nonNullable())]).pipe(
      switchMap(([userInfo, selectedContext]) =>
        isIndividual(selectedContext)
          ? of(userInfo.AgentProfile)
          : this.agencyProfileService.getAgencyProfileDetails(selectedContext.profileCode),
      ),
      nonNullable(),
      rmaShareReplay(),
    );
  }

  private getLegacyCurrentUser(): Observable<UserProfile> {
    return this.httpClient.get<UserProfile>(this.rmaApiUrl('v1/Users/Me'));
  }

  @cache
  public get userInfo$(): Observable<UserProfile | null> {
    return this.userProfile$.asObservable();
  }

  /**
   * @deprecated The method should not be used. Use CurrentUserService.CurrentUserContext or DashboardProfileUserContextService.currentDashboardContext
   */
  @cache
  public get currentAgency$(): Observable<AgencyProfileDetails | null> {
    return this.currentProfile$.pipe(
      map((x) => {
        // SAFETY(legacy): unreviewed, pre-dates this rule.
        if ((x as LegacyUserAgentDetails).AgentCode) {
          return null;
        }
        // SAFETY(legacy): unreviewed, pre-dates this rule.
        return x as AgencyProfileDetails;
      }),
    );
  }

  /**
   * @deprecated The method should not be used. Don't reference AgencyId, convert your API to use Agency Code
   */
  @cache
  public get currentAgencyId$(): Observable<string | null> {
    return this.currentAgency$.pipe(map((agency) => (agency ? agency.AgencyId : null)));
  }

  /**
   * @deprecated The method should not be used.Use CurrentUserService.CurrentUserContext or DashboardProfileUserContextService.currentDashboardContext
   */
  @cache
  public get currentAgent$(): Observable<LegacyUserAgentDetails | null> {
    return this.userInfo$.pipe(
      nonNullable(),
      map((userInfo) => userInfo.AgentProfile),
    );
  }
}
